import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '../../hooks/useMediaQuery';
import s from './PracticeChoiceModal.module.css';

const isCjk = (ch) => ch && /[一-鿿㐀-䶿]/.test(ch);

export default function PracticeChoiceModal({ isOpen, onClose, story }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 1200px)');

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const allChars = useMemo(() => story?.characters || [], [story]);

  const newChars = useMemo(
    () => allChars.filter((c) => c.display_state === 'new_introduction' && isCjk(c.char)),
    [allChars]
  );

  const newAndPracticingChars = useMemo(() => {
    const seen = new Set();
    return allChars.filter((c) => {
      // to_learn characters surface as either 'new_introduction' (new this
      // story) or 'reinforcement' (re-encountered to_learn) — both are
      // practiceable. 'known' stays excluded. (FP-PRACTICE-POOL-01)
      if (
        c.display_state !== 'new_introduction' &&
        c.display_state !== 'practicing' &&
        c.display_state !== 'reinforcement'
      ) return false;
      if (!isCjk(c.char)) return false;
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [allChars]);

  if (!isOpen) return null;

  function handleNewChars() {
    if (newChars.length === 0) return;
    navigate('/practice', {
      state: { seedChars: newChars, autoStart: true },
    });
  }

  function handleNewAndPracticing() {
    if (newAndPracticingChars.length === 0) return;
    navigate('/practice', {
      state: { seedChars: newAndPracticingChars, autoStart: true },
    });
  }

  function handlePrintWorksheet() {
    if (allChars.length === 0) return;
    if (isDesktop) {
      const characterIds = allChars
        .slice(0, 5)
        .map((c) => c.id || c.character_id);
      navigate('/workbook', {
        state: {
          worksheetModal: { characterIds, step: 2 },
        },
      });
    } else {
      navigate('/workbook/select-characters', { state: { story } });
    }
  }

  const newDisabled = newChars.length === 0;
  const newAndPracticingDisabled = newAndPracticingChars.length === 0;
  const worksheetDisabled = allChars.length === 0;

  return (
    <div className={s.root} role="dialog" aria-modal="true" aria-labelledby="practice-modal-title">
      <div className={s.scrim} onClick={onClose} aria-hidden="true" />
      <div className={s.panel}>
        <div className={s.header}>
          <p className={s.eyebrow}>{t('practice.modal.eyebrow')}</p>
          <h2 className={s.title} id="practice-modal-title">
            {t('practice.modal.title')}
          </h2>
        </div>

        <div className={s.options}>
          <button
            type="button"
            onClick={handleNewChars}
            disabled={newDisabled}
            className={`${s.option} ${s.optionHighlighted}${newDisabled ? ` ${s.optionDisabled}` : ''}`}
          >
            <span className={s.optionTitle}>{t('practice.modal.newChars')}</span>
            <span className={s.optionMeta}>
              {t('practice.modal.charCount', { count: newChars.length })}
            </span>
          </button>

          <button
            type="button"
            onClick={handleNewAndPracticing}
            disabled={newAndPracticingDisabled}
            className={`${s.option}${newAndPracticingDisabled ? ` ${s.optionDisabled}` : ''}`}
          >
            <span className={s.optionTitle}>{t('practice.modal.newAndPracticing')}</span>
            <span className={s.optionMeta}>
              {t('practice.modal.charCount', { count: newAndPracticingChars.length })}
            </span>
          </button>

          <div className={s.divider} aria-hidden="true">
            <span className={s.dividerText}>{t('practice.modal.or')}</span>
          </div>

          <button
            type="button"
            onClick={handlePrintWorksheet}
            disabled={worksheetDisabled}
            className={`${s.option}${worksheetDisabled ? ` ${s.optionDisabled}` : ''}`}
          >
            <span className={s.optionTitle}>{t('practice.modal.worksheet')}</span>
            <span className={s.optionMeta}>{t('practice.modal.worksheetDescription')}</span>
          </button>
        </div>

        <button
          type="button"
          onClick={onClose}
          className={s.closeBtn}
        >
          {t('practice.modal.close')}
        </button>
      </div>
    </div>
  );
}
